import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// 1. Global variables to hold the context setter functions.
// These are exported and used by the API service layer (apiServices.js).
let globalIncrementLoading = () => { };
let globalDecrementLoading = () => { };

export const apiIncrementLoading = () => globalIncrementLoading();
export const apiDecrementLoading = () => globalDecrementLoading();

// Define the shape of the context state exposed to children components
interface LoadingContextType {
    isLoading: boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

// Provider component to wrap the entire app
export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Use a counter to handle multiple simultaneous API calls
    const [loadingCount, setLoadingCount] = useState(0);

    const incrementLoading = () => {
        setLoadingCount(prev => prev + 1);
    };

    const decrementLoading = () => {
        setLoadingCount(prev => Math.max(0, prev - 1));
    };

    const isLoading = loadingCount > 0;

    // 2. Set the global functions when the Provider mounts
    useEffect(() => {
        globalIncrementLoading = incrementLoading;
        globalDecrementLoading = decrementLoading;
    }, []); // Run only once on mount

    return (
        <LoadingContext.Provider value={{ isLoading }}>
            {children}
        </LoadingContext.Provider>
    );
};

// Hook for components to access the loading state
export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (context === undefined) {
        throw new Error('useLoading must be used within a LoadingProvider');
    }
    return context;
};

// Global Loading Indicator Component (intended for local, scoped use now)
export const LoadingOverlay: React.FC = () => {
    const { isLoading } = useLoading();

    if (!isLoading) return null;

    // ADDED min-h-[200px] to ensure a minimum size for the overlay
    return (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-neutral-900 bg-opacity-70 dark:bg-opacity-70 transition-opacity duration-300 backdrop-blur-sm min-h-[200px]">
            {/* NEW: A relative container to stack two spinners perfectly in the center */}
            <div className="relative flex items-center justify-center w-16 h-16">
                {/* Spinner 1 (Outer, larger spin) */}
                <div className="absolute animate-spin rounded-full h-16 w-16 border-4 border-dashed border-primary-600 dark:border-primary-400 border-opacity-75"></div>
                {/* Spinner 2 (Inner, smaller solid spin) - The new spinner added in the middle */}
                <div className="absolute animate-spin rounded-full h-8 w-8 border-t-3 border-l-2 border-solid border-blue-500 dark:border-blue-300"></div>
            </div>
        </div>
    );
}